import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Send, ArrowLeft, Heart, AlertTriangle, Bot, User, Loader2 } from "lucide-react";
import axios from "axios";
import Navigation from "@/components/Navigation";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ChatPage = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(() => {
    const saved = localStorage.getItem("brivonb_chat_session");
    return saved || null;
  });
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (!messages.length) {
      setMessages([{
        role: "assistant",
        content: "Hi there. I am Hope, your AI Recovery Companion from ThryvLife, created by Brian Bass (BriVonB). I am here to listen, support, and walk alongside you. Whatever you are feeling right now is valid. How can I help you today?\n\nRemember: I am an AI companion, not a therapist. If you are in crisis, please call 988 or 1-800-799-7233."
      }]);
    }
  }, []);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: userMsg }]);
    setLoading(true);

    try {
      const res = await axios.post(`${API}/chat`, {
        message: userMsg,
        session_id: sessionId,
      });
      const newSessionId = res.data.session_id;
      setSessionId(newSessionId);
      localStorage.setItem("brivonb_chat_session", newSessionId);
      setMessages(prev => [...prev, { role: "assistant", content: res.data.response }]);
    } catch {
      setMessages(prev => [...prev, {
        role: "assistant",
        content: "I am sorry, I am having trouble connecting right now. Please try again in a moment. If you need immediate help, please call 988 or text HOME to 741741."
      }]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex flex-col">
      <Navigation />

      <div className="flex-1 flex flex-col pt-20 max-w-3xl mx-auto w-full px-4">
        {/* Header */}
        <div className="py-4 flex items-center gap-4">
          <Link to="/" className="text-[#4FB3D4] hover:text-[#4FB3D4]/80">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#4FB3D4] to-[#A69BE0] flex items-center justify-center">
              <Heart className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="font-heading text-lg font-semibold text-[#1A2A3A]" data-testid="chat-title">Hope - Recovery Companion</h1>
              <p className="text-xs text-[#1A2A3A]/50">AI Support | Free | Available 24/7</p>
            </div>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-4 flex items-start gap-2" data-testid="chat-disclaimer">
          <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-700">
            Hope is an AI companion, not a licensed therapist. She cannot diagnose or cure any condition. In a crisis, call <strong>988</strong> or <strong>1-800-799-7233</strong>.
          </p>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto space-y-4 pb-4" data-testid="chat-messages">
          {messages.map((msg, i) => (
            <div key={i} className={`flex gap-3 ${msg.role === "user" ? "flex-row-reverse" : ""}`} data-testid={`chat-msg-${i}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${msg.role === "user" ? "bg-[#4FB3D4]/10" : "bg-gradient-to-br from-[#4FB3D4] to-[#A69BE0]"}`}>
                {msg.role === "user" ? <User className="h-4 w-4 text-[#4FB3D4]" /> : <Bot className="h-4 w-4 text-white" />}
              </div>
              <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${msg.role === "user" ? "bg-[#4FB3D4] text-white rounded-br-md" : "bg-white border border-[#E5E7EB]/50 text-[#1A2A3A]/80 rounded-bl-md shadow-sm"}`}>
                <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex gap-3" data-testid="chat-loading">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#4FB3D4] to-[#A69BE0] flex items-center justify-center">
                <Bot className="h-4 w-4 text-white" />
              </div>
              <div className="bg-white border border-[#E5E7EB]/50 rounded-2xl rounded-bl-md px-4 py-3 shadow-sm">
                <Loader2 className="h-5 w-5 text-[#A69BE0] animate-spin" />
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="sticky bottom-0 bg-[#FDFBF7] pt-2 pb-4" data-testid="chat-input-area">
          <div className="flex gap-2 items-end bg-white rounded-2xl border border-[#E5E7EB]/50 shadow-card p-2">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Share what is on your mind..."
              className="flex-1 resize-none border-0 bg-transparent outline-none text-sm text-[#1A2A3A] placeholder:text-[#1A2A3A]/30 p-2 max-h-32"
              rows={1}
              data-testid="chat-input"
            />
            <Button
              onClick={sendMessage}
              disabled={!input.trim() || loading}
              className="bg-[#4FB3D4] hover:bg-[#4FB3D4]/90 text-white rounded-xl px-4 py-2 h-10"
              data-testid="chat-send-btn"
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
          <p className="text-[10px] text-[#1A2A3A]/30 text-center mt-2">
            Hope remembers your conversation in this session. Start fresh by clearing your browser data.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ChatPage;
